/* Uebernommen aus der Content-Factory (components/loading-animation/image-spinner.tsx), Auftrag
   2026-10-13. Inline-SVG mit eigenen Keyframes, Farben aus EDS-Tokens. */
import type { CSSProperties } from 'react';

type SpinnerProps = {
  speed?: number;
  className?: string;
  style?: CSSProperties;
  label?: string;
};

const KEYFRAMES = `
@keyframes cfScan { 0%,100% { transform: translateX(0); } 50% { transform: translateX(17px); } }
@keyframes cfBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }
@keyframes cfBrush {
  0%   { transform: translate(0,6px) rotate(6deg); }
  7%   { transform: translate(-2px,10px) rotate(11deg); }
  14%  { transform: translate(-2px,0) rotate(-21deg); }
  36%  { transform: translate(0,0) rotate(7deg); }
  42%  { transform: translate(6px,-2px) rotate(-20deg); }
  62%  { transform: translate(8px,0) rotate(8deg); }
  68%  { transform: translate(13px,-2px) rotate(-19deg); }
  88%  { transform: translate(14px,0) rotate(9deg); }
  100% { transform: translate(0,6px) rotate(6deg); }
}
@keyframes cfDraw1 { 0%,14% { stroke-dashoffset: 70; opacity: 1; } 36%,92% { stroke-dashoffset: 0; opacity: 1; } 100% { stroke-dashoffset: 0; opacity: 0; } }
@keyframes cfDraw2 { 0%,42% { stroke-dashoffset: 70; opacity: 1; } 62%,92% { stroke-dashoffset: 0; opacity: 1; } 100% { stroke-dashoffset: 0; opacity: 0; } }
@keyframes cfDraw3 { 0%,68% { stroke-dashoffset: 70; opacity: 1; } 88%,92% { stroke-dashoffset: 0; opacity: 1; } 100% { stroke-dashoffset: 0; opacity: 0; } }
@keyframes cfEaselShake { 0%,100% { transform: translate(0,0); } 36% { transform: translate(1px,0); } 62% { transform: translate(-1px,1px); } 88% { transform: translate(1px,0); } }
@keyframes cfPalette { 0%,100% { transform: rotate(-2deg); } 50% { transform: rotate(3deg); } }
@media (prefers-reduced-motion: reduce) { [data-cf-spin] * { animation-play-state: paused !important; } }
`;

export function ImageSpinner({ speed = 1, className, style, label }: SpinnerProps) {
  const a = (
    name: string,
    dur: number,
    ease: string,
    extra: CSSProperties = {},
  ): CSSProperties => ({
    animation: `${name} ${dur / speed}s ${ease} infinite`,
    ...extra,
  });
  const box = (origin: string): CSSProperties => ({
    transformBox: 'fill-box',
    transformOrigin: origin,
  });

  return (
    <svg
      data-cf-spin="bild"
      viewBox="0 0 240 170"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={className}
      style={{ width: '100%', maxWidth: '15.75rem', height: 'auto', overflow: 'visible', ...style }}
    >
      <style>{KEYFRAMES}</style>
      <rect
        x="16"
        y="130"
        width="208"
        height="7"
        rx="3.5"
        fill="var(--eds-semantic-color-gray-900)"
      />
      <g style={a('cfEaselShake', 3.2, 'ease-in-out')}>
        <path
          d="M140 100 L128 132"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="5"
          strokeLinecap="round"
        />
        <path
          d="M200 100 L212 132"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="5"
          strokeLinecap="round"
        />
        <path
          d="M136 112 L204 112"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <rect
          x="126"
          y="34"
          width="88"
          height="68"
          rx="3"
          fill="var(--eds-semantic-color-background-primary)"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="3"
        />
        <path
          d="M148 48 C153 62, 145 74, 151 92"
          stroke="var(--eds-semantic-color-accent-primary-800)"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
          strokeDasharray="70"
          strokeDashoffset="70"
          style={a('cfDraw1', 3.2, 'ease-in-out')}
        />
        <path
          d="M170 46 C175 60, 167 74, 173 90"
          stroke="var(--eds-semantic-color-accent-secondary-800)"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
          strokeDasharray="70"
          strokeDashoffset="70"
          style={a('cfDraw2', 3.2, 'ease-in-out')}
        />
        <path
          d="M192 50 C197 62, 189 76, 195 90"
          stroke="var(--eds-semantic-color-status-warning-400)"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
          strokeDasharray="70"
          strokeDashoffset="70"
          style={a('cfDraw3', 3.2, 'ease-in-out')}
        />
      </g>
      <g style={a('cfPalette', 1.6, 'ease-in-out', box('50% 100%'))}>
        <ellipse
          cx="46"
          cy="124"
          rx="28"
          ry="12"
          fill="var(--eds-semantic-color-gray-100)"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="2.5"
        />
        <circle cx="34" cy="121" r="4" fill="var(--eds-semantic-color-accent-primary-800)" />
        <circle cx="46" cy="125" r="4" fill="var(--eds-semantic-color-accent-secondary-800)" />
        <circle cx="58" cy="121" r="4" fill="var(--eds-semantic-color-status-warning-400)" />
      </g>
      <rect
        x="28"
        y="84"
        width="44"
        height="46"
        rx="11"
        fill="var(--eds-semantic-color-accent-secondary-800)"
      />
      <rect
        x="44"
        y="96"
        width="12"
        height="12"
        rx="3"
        fill="var(--eds-semantic-color-accent-secondary-100)"
      />
      <g>
        <path
          d="M50 42 L50 30"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle
          cx="50"
          cy="26"
          r="5"
          fill="var(--eds-semantic-color-accent-primary-800)"
          style={a('cfBlink', 1, 'ease-in-out')}
        />
        <rect
          x="24"
          y="42"
          width="52"
          height="40"
          rx="11"
          fill="var(--eds-semantic-color-gray-100)"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="3"
        />
        <rect
          x="32"
          y="54"
          width="36"
          height="16"
          rx="8"
          fill="var(--eds-semantic-color-accent-secondary-800)"
        />
        <circle
          cx="41"
          cy="62"
          r="4"
          fill="var(--eds-semantic-color-background-primary)"
          style={a('cfScan', 2.2, 'ease-in-out')}
        />
      </g>
      <g style={a('cfBrush', 3.2, 'ease-in-out', box('0% 100%'))}>
        <path
          d="M70 96 L124 76"
          stroke="var(--eds-semantic-color-accent-secondary-800)"
          strokeWidth="10"
          strokeLinecap="round"
        />
        <path
          d="M124 76 L146 68"
          stroke="var(--eds-semantic-color-gray-300)"
          strokeWidth="6"
          strokeLinecap="round"
        />
        <path
          d="M144 69 L154 65"
          stroke="var(--eds-semantic-color-accent-primary-800)"
          strokeWidth="9"
          strokeLinecap="round"
        />
      </g>
      <circle cx="70" cy="96" r="7" fill="var(--eds-semantic-color-accent-secondary-800)" />
    </svg>
  );
}
